import {
  HumanMessage,
  AIMessage,
  ToolMessage,
  isHumanMessage,
  type BaseMessage,
  type MessageContent,
} from "@langchain/core/messages";
import { Turn } from "./turns";

export type RecordWithMessages = {
  messages?: BaseMessage[];
};

// ---------------------------------------------------------------------------
// TaggedMessage — generic XML-tag wrapper for distinguishable BaseMessages.
//
// A tagged message is a BaseMessage whose textual content is wrapped in a
// stable XML tag (e.g. `<system-reminder>...</system-reminder>`,
// `<tool_use_error>...</tool_use_error>`) so the model can distinguish
// authoritative system signals from user speech, recoverable errors from
// real model output, etc. Claude Code uses the same pattern for
// `<tool_use_error>` — the wrapper is post-trained as a stable recovery
// signal.
//
// Why a generic instead of bespoke factories per tag:
//   1. One implementation of wrap/unwrap/isInstance with consistent
//      semantics (idempotent wrap, passes arrays through, returns null
//      on empty).
//   2. A central registry that the prompt module reads to render a
//      one-time explanation of every tag the agent might see.
//   3. New tags become a single `createTaggedMessage(...)` call instead
//      of recreating the SystemReminder skeleton each time.
//
// `createTaggedMessage` returns a `TaggedMessage<C>` namespace with
// `wrap`, `unwrap`, `create`, `isInstance`, plus the static `tag` and
// `description` metadata. SystemReminder builds atop this primitive and
// adds `reminderType`-specific behaviour (sub-typed lookup, throttled
// reminder middleware integration) — see `createSystemReminder` below.
// ---------------------------------------------------------------------------

export interface TaggedMessageDef<C extends BaseMessage> {
  /** Tag name (no angle brackets). E.g. "system-reminder", "tool_use_error". */
  tag: string;
  /** Base message class — HumanMessage / AIMessage / ToolMessage. */
  baseClass: { new (fields: any): C; isInstance(m: BaseMessage): m is C };
  /** Prompt-rendered description telling the agent how to interpret this tag. */
  description: string;
  /** Optional `name` field stamped on the constructed message (SystemReminder uses "context"). */
  name?: string;
  /**
   * Skip auto-registration in the global tagged-message registry. Use for
   * tags that have custom `isInstance` semantics and prefer to register
   * themselves manually with the tweaked entry. Default: false.
   */
  hidden?: boolean;
}

export interface TaggedMessage<C extends BaseMessage> {
  readonly tag: string;
  readonly description: string;
  /**
   * Wrap `content` in `<tag>...</tag>`. Idempotent (already-wrapped strings
   * pass through). Arrays (content-block form, e.g. cache_control attached
   * by promptCachingMiddleware) pass through unchanged. Returns null for
   * empty / whitespace-only input.
   */
  wrap(content: MessageContent): MessageContent | null;
  /** Strip `<tag>...</tag>`. Inverse of `wrap` for string inputs. */
  unwrap(content: string): string;
  /**
   * Construct a tagged instance of the base class. Throws if `content` is
   * empty or whitespace-only.
   */
  create(fields: TaggedMessageCreateFields): C;
  /** Type guard: msg is an instance of this tag. */
  isInstance(msg: BaseMessage): msg is C;
}

export interface TaggedMessageCreateFields {
  content: string;
  id?: string;
  additional_kwargs?: Record<string, any>;
  /** For ToolMessage-based tags (required by the ToolMessage constructor). */
  tool_call_id?: string;
}

const TAGGED_MESSAGE_REGISTRY: TaggedMessage<any>[] = [];

/**
 * Register a tagged message so the prompt module can describe it to the
 * agent. Idempotent on `tag` — re-registering the same tag replaces the
 * prior entry (useful when a wrapper namespace overrides the primitive's
 * isInstance with stricter semantics).
 */
export function registerTaggedMessage(entry: TaggedMessage<any>): void {
  const idx = TAGGED_MESSAGE_REGISTRY.findIndex((t) => t.tag === entry.tag);
  if (idx >= 0) {
    TAGGED_MESSAGE_REGISTRY[idx] = entry;
  } else {
    TAGGED_MESSAGE_REGISTRY.push(entry);
  }
}

/** All currently-registered tagged messages, in registration order. */
export function getTaggedMessages(): readonly TaggedMessage<any>[] {
  return TAGGED_MESSAGE_REGISTRY;
}

/**
 * Build a TaggedMessage namespace for a given tag + base class.
 *
 * @param def - tag name, base message class, description, optional `name`
 *              field, optional `hidden` to skip auto-registration.
 * @returns A TaggedMessage<C> with `wrap`, `unwrap`, `create`, `isInstance`.
 */
export function createTaggedMessage<C extends BaseMessage>(
  def: TaggedMessageDef<C>
): TaggedMessage<C> {
  const open = `<${def.tag}>`;
  const close = `</${def.tag}>`;

  const tagged: TaggedMessage<C> = {
    tag: def.tag,
    description: def.description,

    wrap(content) {
      if (Array.isArray(content)) {
        return content.length === 0 ? null : content;
      }
      if (typeof content !== "string") return null;
      if (!content || content.trim().length === 0) return null;
      if (content.startsWith(open)) return content;
      return `${open}\n${content}\n${close}`;
    },

    unwrap(content) {
      if (!content.startsWith(open)) return content;
      const inner = content.slice(open.length);
      const end = inner.lastIndexOf(close);
      return (end === -1 ? inner : inner.slice(0, end)).trim();
    },

    create(fields) {
      const wrapped = tagged.wrap(fields.content);
      if (wrapped === null) {
        throw new Error(
          `TaggedMessage(${def.tag}): content must be non-empty`
        );
      }
      const init: Record<string, any> = {
        content: wrapped,
        id: fields.id ?? crypto.randomUUID(),
        additional_kwargs: fields.additional_kwargs ?? {},
      };
      if (def.name) init.name = def.name;
      if (fields.tool_call_id !== undefined) init.tool_call_id = fields.tool_call_id;
      return new def.baseClass(init);
    },

    isInstance(msg): msg is C {
      if (!def.baseClass.isInstance(msg)) return false;
      if (def.name !== undefined && msg.name !== def.name) return false;
      const content =
        typeof msg.content === "string"
          ? msg.content
          : Array.isArray(msg.content)
            ? // Content-block form — check the first text block (where the
              // tag lives after promptCaching attaches cache_control to it).
              (msg.content.find(
                (b: any) => b && b.type === "text" && typeof b.text === "string"
              ) as { text: string } | undefined)?.text ?? ""
            : "";
      return typeof content === "string" && content.startsWith(open);
    },
  };

  if (!def.hidden) registerTaggedMessage(tagged);
  return tagged;
}

// ---------------------------------------------------------------------------
// Context message + SystemReminder primitives
//
// Two layers:
//
//   1. A "context message" is any HumanMessage with `name="context"`. The
//      frontend filters them out (see use-langgraph-stream's
//      filterContextMessages). On the server they live in state alongside
//      real human/AI messages.
//
//   2. A SystemReminder is a context message whose body is wrapped in
//      `<system-reminder>...</system-reminder>` tags so the model can
//      distinguish injected system context from real user speech.
//
// At LLM-call time, `smooshSystemReminders` merges runs of adjacent
// SystemReminder + HumanMessage into one HumanMessage so Anthropic's
// role-alternation contract holds. State stays unmerged.
//
// Each SystemReminder is tagged with a `reminderType` (e.g. `ADS_NAVIGATION`,
// `ACTIVE_PAGE_FOCUS`). The type lives in `additional_kwargs.reminderType` —
// metadata only, never in the body the model reads. Structured payloads
// providers want to read on later turns live in `additional_kwargs.reminderBody`.
// `SystemReminder.findLast(messages, type)` walks history and returns the most
// recent reminder matching that type, with its body and a turn count.
//
// Why the type is metadata, not text: the body is for the model; the type is
// for deterministic extraction by middleware. Mixing the two leaks plumbing
// (sentinel names like `[ADS_NAVIGATION]`) into the model's prompt where it
// serves no purpose.
// ---------------------------------------------------------------------------

export const CONTEXT_MESSAGE_NAME = "context";

/** kwarg key for the SystemReminder type tag (e.g. `"ADS_NAVIGATION"`). */
export const REMINDER_TYPE_KWARG = "reminderType";

/** kwarg key for an optional structured payload that future turns can read
 *  via `SystemReminder.findLast`. The model never sees it. */
export const REMINDER_BODY_KWARG = "reminderBody";

/**
 * kwarg key for the intent payload on an IntentMessage. The presence of
 * this kwarg is what distinguishes an IntentMessage from any other
 * context-tagged HumanMessage.
 */
export const INTENT_KWARG = "intent";

/**
 * Construct a context message (HumanMessage with `name="context"`).
 *
 * Accepts both call shapes for back-compat with the legacy SDK:
 *   - `{ id?, additional_kwargs?, timestamp? }` (preferred — internal)
 *   - `{ timestamp? }`                          (legacy SDK shape)
 * `timestamp`, when present, is folded into `additional_kwargs.timestamp`.
 *
 * Each message gets a unique UUID. messagesStateReducer auto-generates
 * IDs when missing, but stamping at construction is defense-in-depth —
 * anything bypassing the reducer (manual pushes, custom middleware)
 * still gets a unique ID. Same UUIDs let langgraph-sdk's
 * MessageTupleManager stream reminders as separate chunks instead of
 * concatenating into one.
 */
export function createContextMessage(
  text: string,
  options?: { id?: string; additional_kwargs?: Record<string, any>; timestamp?: string }
): HumanMessage {
  const additional_kwargs = {
    ...(options?.additional_kwargs ?? {}),
    ...(options?.timestamp ? { timestamp: options.timestamp } : {}),
  };
  return new HumanMessage({
    content: text,
    name: CONTEXT_MESSAGE_NAME,
    id: options?.id ?? crypto.randomUUID(),
    additional_kwargs,
  });
}

/**
 * Multimodal variant of {@link createContextMessage} — accepts content blocks
 * (e.g. images) instead of a string. Same UUID/timestamp guarantees.
 */
export function createMultimodalContextMessage(
  content: MessageContent,
  options?: { id?: string; additional_kwargs?: Record<string, any>; timestamp?: string }
): HumanMessage {
  const additional_kwargs = {
    ...(options?.additional_kwargs ?? {}),
    ...(options?.timestamp ? { timestamp: options.timestamp } : {}),
  };
  return new HumanMessage({
    content,
    name: CONTEXT_MESSAGE_NAME,
    id: options?.id ?? crypto.randomUUID(),
    additional_kwargs,
  });
}

export function isContextMessage(msg: BaseMessage): boolean {
  return msg.name === CONTEXT_MESSAGE_NAME;
}

/**
 * `isMessageVisibleToUser` — single canonical predicate for "does this
 * message render in the chat UI?"
 *
 * The chat UI's presentation layer drops every message with
 * `name === "context"` before rendering — SystemReminders, summaries,
 * intent events, and our hidden agent-runtime / provider error tags
 * all stay out of the user's view. This helper names that convention
 * so tests can write `expect(visible.map(...)).not.toContain(...)`
 * without each caller re-deriving "what does the user see?"
 *
 * Use this in tests AND in any backend code that needs to reason about
 * the user's view (e.g. generating a recovery message that references
 * what the user already saw). Do not invert it; "user-visible" is the
 * affirmative concept worth naming.
 */
export function isMessageVisibleToUser(msg: BaseMessage): boolean {
  return !isContextMessage(msg);
}

/**
 * Returns only the messages the user will see in the chat UI — the
 * inverse of the context messages the frontend hides. Tests live here
 * (rather than reaching into a UI package) so backend assertions about
 * the user's view don't drift from the conventions defined alongside
 * the other context-message primitives in this module.
 */
export function userVisibleMessages(messages: BaseMessage[]): BaseMessage[] {
  return messages.filter(isMessageVisibleToUser);
}

/** Marker used at the start of compacted-conversation summary messages. */
export const SUMMARY_MARKER = "[[[CONVERSATION SUMMARY]]]";

/**
 * Type guard for compaction summary messages — context messages whose body
 * represents a compacted prior conversation. Identifies via the
 * `additional_kwargs.isSummary` flag (preferred) or content markers
 * (backwards-compatible).
 */
export function isSummaryMessage(msg: BaseMessage): boolean {
  if (!isContextMessage(msg)) return false;
  if (msg.additional_kwargs?.isSummary === true) return true;
  const content = typeof msg.content === "string" ? msg.content : "";
  return content.includes("[Conversation Summary]") || content.includes(SUMMARY_MARKER);
}

export function isSystemReminder(msg: BaseMessage): boolean {
  return (
    msg.name === CONTEXT_MESSAGE_NAME &&
    typeof msg.content === "string" &&
    msg.content.startsWith("<system-reminder>")
  );
}

export interface FoundReminder<T = unknown> {
  /** The full message content (including <system-reminder> wrapper). */
  content: string;
  /** Structured payload from `additional_kwargs.reminderBody`, or null. */
  body: T | null;
  /** Number of non-context HumanMessage boundaries between the reminder and
   *  the end of the message list. 0 = since the most recent user turn. */
  turnsAgo: number;
  /** The matched message itself, for callers that need its other metadata. */
  message: BaseMessage;
}

/**
 * Tagged context message: a HumanMessage whose content is wrapped in
 * `<system-reminder>` tags so the model can distinguish injected system
 * context from real user speech. The `reminderType` lives in
 * `additional_kwargs.reminderType` for deterministic future-turn lookup.
 *
 * Returned as a plain HumanMessage rather than a subclass so the langgraph
 * SDK wire format always sees `type: "human"`. The frontend filters them
 * via `name === "context"` (use-langgraph-stream's `filterContextMessages`).
 *
 * The namespace below holds the static helpers (`wrap`, `maybe`, `getType`,
 * `findLast`) that used to live on the class.
 */
/**
 * SystemReminder is the canonical tagged message for harness-injected
 * context. Builds atop `createTaggedMessage` for wrap/unwrap, then
 * extends with `reminderType` validation, structured `body` payloads,
 * and turn-aware sub-typed lookups (`findLast`, `findAll`, `filter`).
 *
 * `hidden: true` here — we register a tweaked entry below whose
 * `isInstance` enforces the stricter "has reminderType kwarg" check
 * that distinguishes SystemReminders from IntentMessages and bare
 * context messages.
 */
const systemReminderPrimitive = createTaggedMessage<HumanMessage>({
  tag: "system-reminder",
  baseClass: HumanMessage,
  name: CONTEXT_MESSAGE_NAME,
  description:
    "Authoritative context injected by the harness — error states, the page the user is viewing, recent user actions, loaded skills. " +
    "Treat these as the system speaking, not the user; they exist to give you live state the conversation alone doesn't reveal. " +
    "Read them carefully and let them inform your next action. Do not quote them back to the user or narrate that you saw them.",
  hidden: true,
});

export function createSystemReminder(fields: {
  // Optional in reconstruction mode — middleware (e.g. promptCachingMiddleware)
  // spread-clones an existing reminder. The reminderType already lives in
  // additional_kwargs from the prior construction; accept it from there when
  // the top-level field is missing.
  reminderType?: string;
  // Content can be a string (initial construction) or array of content blocks
  // (reconstruction after middleware mutation, e.g. cache_control attachment;
  // or multimodal reminders that surface images alongside text).
  content: MessageContent;
  body?: unknown;
  additional_kwargs?: Record<string, any>;
  id?: string;
}): HumanMessage {
  const inheritedKwargs = fields.additional_kwargs ?? {};
  const reminderType =
    fields.reminderType ?? (inheritedKwargs[REMINDER_TYPE_KWARG] as string | undefined);
  if (!reminderType || reminderType.trim().length === 0) {
    throw new Error("SystemReminder: reminderType must be non-empty");
  }

  const tagged = systemReminderPrimitive.wrap(fields.content);
  if (tagged === null) {
    throw new Error("SystemReminder: content must be non-empty");
  }

  const kwargs: Record<string, any> = {
    ...inheritedKwargs,
    [REMINDER_TYPE_KWARG]: reminderType,
  };
  if (fields.body !== undefined) {
    kwargs[REMINDER_BODY_KWARG] = fields.body;
  }

  return new HumanMessage({
    content: tagged as any,
    name: CONTEXT_MESSAGE_NAME,
    // See createContextMessage: each reminder needs a unique id or langgraph
    // assigns the same run id to every emitted message and the frontend SDK
    // merges them into one chunk.
    id: fields.id ?? crypto.randomUUID(),
    additional_kwargs: kwargs,
  });
}

/**
 * Static helpers for creating and inspecting SystemReminder context messages.
 *
 * Usage:
 *   SystemReminder.create({ reminderType: "ADS_NAVIGATION", content: "...", body: {...} })
 *   SystemReminder.maybe({ reminderType: "...", content: "..." }) // null on empty
 *   SystemReminder.findLast(messages, "ADS_NAVIGATION")
 *   SystemReminder.getType(msg)
 *   SystemReminder.wrap("...") // "<system-reminder>\n...\n</system-reminder>"
 */
export const SystemReminder = {
  tag: systemReminderPrimitive.tag,
  description: systemReminderPrimitive.description,

  create: createSystemReminder,

  /** Alias of create() for callers using the class-style import path. */
  new: createSystemReminder,

  isInstance: (msg: BaseMessage): msg is HumanMessage => {
    // A SystemReminder requires the `reminderType` kwarg — that's what
    // separates it from other context-tagged messages (IntentMessage,
    // image events, summaries). Without this check, every context
    // message would read as a SystemReminder.
    if (msg.name !== CONTEXT_MESSAGE_NAME) return false;
    if (!HumanMessage.isInstance(msg)) return false;
    if (!ContextMessage.isInstance(msg)) return false;
    return typeof msg.additional_kwargs?.[REMINDER_TYPE_KWARG] === "string";
  },

  /** Delegated to the TaggedMessage primitive. See `createTaggedMessage`. */
  wrap: systemReminderPrimitive.wrap,

  /** Delegated to the TaggedMessage primitive. See `createTaggedMessage`. */
  unwrap: systemReminderPrimitive.unwrap,

  /** Returns a SystemReminder HumanMessage, or null if content is empty. */
  maybe(fields: {
    reminderType: string;
    content: string;
    body?: unknown;
    additional_kwargs?: Record<string, any>;
    id?: string;
  }): HumanMessage | null {
    if (!fields.content || fields.content.trim().length === 0) return null;
    return createSystemReminder(fields);
  },

  /** Returns the reminderType tag for `msg`, or null if it isn't tagged. */
  getType(msg: BaseMessage): string | null {
    const t = msg.additional_kwargs?.[REMINDER_TYPE_KWARG];
    return typeof t === "string" ? t : null;
  },

  isType(msg: BaseMessage, reminderType: string): boolean {
    return SystemReminder.getType(msg) === reminderType;
  },

  notType(msg: BaseMessage, reminderType: string): boolean {
    return !SystemReminder.isType(msg, reminderType);
  },

  filter(
    messages: BaseMessage[] | undefined,
    reminderType?: string
  ): HumanMessage[] {
    return messages
      ? messages.filter(
          (msg): msg is HumanMessage =>
            HumanMessage.isInstance(msg) &&
            SystemReminder.isInstance(msg) &&
            (!reminderType || SystemReminder.isType(msg, reminderType))
        )
      : [];
  },

  /**
   * Returns the most recent SystemReminder of the given `reminderType`, or
   * null if none found. `turnsAgo` is the number of turns between the turn
   * the reminder belonged to and the current (latest) turn — `0` means the
   * reminder is part of this turn (typical case: a reminder injected just
   * before the most recent user message). `1` means the previous turn, etc.
   *
   * Turn boundaries come from {@link Turn.partition} — a turn is
   * `[context*] [human?] [response*]`, with a new turn starting at every
   * non-context HumanMessage. This is more accurate than counting raw
   * human-message boundaries: a reminder attached to the prelude of the
   * latest human message is in *this* turn, not the previous one.
   */
  findLast<T = unknown>(
    messages: BaseMessage[] | undefined,
    reminderType: string
  ): FoundReminder<T> | null {
    if (!messages || messages.length === 0) return null;

    const turns = Turn.partition(messages);
    if (turns.length === 0) return null;

    for (let i = turns.length - 1; i >= 0; i--) {
      const turn = turns[i]!;
      for (let j = turn.length - 1; j >= 0; j--) {
        const msg = turn[j]!;
        if (SystemReminder.notType(msg, reminderType)) continue;
        const body = (msg.additional_kwargs?.[REMINDER_BODY_KWARG] as T | undefined) ?? null;
        const content = typeof msg.content === "string" ? msg.content : "";
        return { content, body, turnsAgo: turns.length - 1 - i, message: msg };
      }
    }
    return null;
  },

  /**
   * Returns every SystemReminder of `reminderType` in chronological order,
   * each annotated with its `body`, raw `content`, and `turnsAgo`. Same
   * Turn.partition-based accounting as `findLast` so callers comparing
   * "how stale is this batch?" line up with the rest of the conversation
   * machinery. Empty input → empty array.
   */
  findAll<T = unknown>(
    messages: BaseMessage[] | undefined,
    reminderType: string
  ): FoundReminder<T>[] {
    if (!messages || messages.length === 0) return [];
    const turns = Turn.partition(messages);
    if (turns.length === 0) return [];
    const lastIdx = turns.length - 1;
    const out: FoundReminder<T>[] = [];
    for (let i = 0; i <= lastIdx; i++) {
      const turn = turns[i]!;
      for (const msg of turn) {
        if (SystemReminder.notType(msg, reminderType)) continue;
        const body = (msg.additional_kwargs?.[REMINDER_BODY_KWARG] as T | undefined) ?? null;
        const content = typeof msg.content === "string" ? msg.content : "";
        out.push({ content, body, turnsAgo: lastIdx - i, message: msg });
      }
    }
    return out;
  },
};

export const ContextMessage = {
  isInstance: isContextMessage,
};

// Register SystemReminder with the global tagged-message registry. The
// entry uses the namespace's stricter `isInstance` (which requires the
// `reminderType` kwarg) so taggedMessages.ts's prompt rendering and any
// programmatic registry walk discriminate SystemReminders from other
// context-named messages (IntentMessage, summary, image events).
registerTaggedMessage({
  tag: SystemReminder.tag,
  description: SystemReminder.description,
  wrap: SystemReminder.wrap,
  unwrap: SystemReminder.unwrap,
  // The namespace's `create` takes richer fields (reminderType, body)
  // than the generic TaggedMessageCreateFields. The registry only cares
  // about wrap/unwrap/isInstance for prompt rendering; expose the
  // namespace `create` cast to the generic shape for completeness.
  create: SystemReminder.create as TaggedMessage<HumanMessage>["create"],
  isInstance: SystemReminder.isInstance,
});

// ---------------------------------------------------------------------------
// Tagged messages for the harness error-boundary.
//
// `withErrorBoundary` (see app/core/harness/errorBoundary.ts) classifies
// thrown errors into agent_fixable / transient / harness. For the first
// two — the cases the agent should learn from — it appends one of these
// tagged messages to state so the next turn's model call sees a
// recognizable signal instead of the work above getting wiped.
//
// `harness` errors do NOT get a tagged message: that path means OUR
// code is broken, and synthesizing an AI message would falsely attribute
// the failure to the model. The boundary returns a distinct shape
// (`status: "failed"`, `errorKind: "harness"`) so callers can handle it.
// ---------------------------------------------------------------------------

/**
 * `<agent_runtime_error>` — the agent loop hit a runtime limit
 * (recursion limit, malformed state update, etc.).
 *
 * **Hidden from the chat UI** via `name: "context"` — the frontend's
 * `filterContextMessages` drops these, the same way it drops
 * SystemReminders. The user never sees the raw XML tag; instead the
 * error boundary appends a separate user-facing AIMessage with friendly
 * recovery prose. This tagged message exists solely for the agent's
 * NEXT turn — it reads the tag in the message history, knows recursion
 * fired, and behaves accordingly (summarizes prior work, doesn't retry
 * the same loop).
 */
export const AgentRuntimeError = createTaggedMessage<AIMessage>({
  tag: "agent_runtime_error",
  baseClass: AIMessage,
  name: CONTEXT_MESSAGE_NAME,
  description:
    "Your prior tool calls and messages this turn are intact above. The agent runtime hit a limit (recursion, step budget, malformed state). " +
    "Do NOT retry the same loop. On the next user turn, acknowledge briefly what was accomplished and ask the user how they'd like to proceed.",
});

/**
 * `<provider_error>` — the underlying model provider returned a transient
 * failure (overload, rate limit, network blip).
 *
 * **Hidden from the chat UI** (see `AgentRuntimeError` for the
 * rationale). The user sees a friendly "try again" recovery message
 * appended by the error boundary; this tag exists for the agent's next
 * turn to know the failure was provider-side, not user-driven.
 */
export const ProviderError = createTaggedMessage<AIMessage>({
  tag: "provider_error",
  baseClass: AIMessage,
  name: CONTEXT_MESSAGE_NAME,
  description:
    "The model provider had a transient failure (overload, rate limit, connection drop). The prior work in this turn is intact. " +
    "On the next user turn, acknowledge briefly that there was a hiccup and continue from where the work left off if the user asks.",
});

/**
 * `<tool_use_error>` — a tool call failed (thrown, schema rejection,
 * permission denial). The wrapped body is the error string. The agent
 * decides whether to retry with different inputs, change approach, or
 * report what it accomplished and stop. Mirrors Claude Code's
 * post-trained `<tool_use_error>` wrapper.
 *
 * Currently surfaced by `createToolErrorSurfacingMiddleware` via a plain
 * ToolMessage with `status: "error"`; this entry documents the format
 * for the system-prompt explainer and gives a programmatic constructor
 * for places that want to build one directly (e.g. tests, future
 * permission middlewares that prefer the tag-wrapped form).
 */
export const ToolUseError = createTaggedMessage<ToolMessage>({
  tag: "tool_use_error",
  baseClass: ToolMessage,
  description:
    "A tool call failed. The body explains why. Decide your next move based on the error: adjust inputs and retry the same tool, switch approach, " +
    "or — if the failure is fundamental (auth, missing capability) — report it to the user and stop. Do not loop on the same failed call.",
});

// ---------------------------------------------------------------------------
// IntentMessage — a subset of context messages.
//
// SystemReminders carry instructions for the agent. IntentMessages record a
// user-driven UI action (switch_page, refresh_assets, etc.) so the
// transcript captures what the user *did* even when they didn't type.
// Both share the `name="context"` tag so the frontend filter hides them
// from the chat UI; what distinguishes an IntentMessage is the
// `additional_kwargs.intent` payload.
//
// Internal tools tell the difference via `IntentMessage.isInstance`. In
// particular, `Turn.partition` treats IntentMessages as turn boundaries
// (a UI click is a turn even though the user said nothing) while plain
// context attaches forward to the next turn.
// ---------------------------------------------------------------------------

/** Type guard: true if `msg` is a context message carrying an intent payload. */
export function isIntentMessage(msg: BaseMessage): boolean {
  if (!isContextMessage(msg)) return false;
  return msg.additional_kwargs?.[INTENT_KWARG] != null;
}

/**
 * Render an intent payload as a single-line debug string used as the
 * IntentMessage's content. The model never sees it (frontend hides
 * context, conversation prep filters it out before LLM dispatch), but
 * traces, replay tools, and snapshot tests all benefit from human-
 * readable bodies.
 */
function describeIntent(intent: unknown): string {
  if (intent && typeof intent === "object") {
    const obj = intent as Record<string, unknown>;
    if (typeof obj.type === "string") {
      const payload = obj.payload;
      if (payload && typeof payload === "object") {
        const stage = (payload as Record<string, unknown>).stage;
        if (typeof stage === "string") return `[user action: ${obj.type} → ${stage}]`;
      }
      return `[user action: ${obj.type}]`;
    }
  }
  return `[user action]`;
}

/**
 * Build an IntentMessage that records a user UI action. The intent is
 * stamped on `additional_kwargs.intent` for introspection; the rendered
 * content is a debug string that's safe to read in traces.
 */
export function createIntentMessage(
  intent: unknown,
  options?: { id?: string; additional_kwargs?: Record<string, any>; timestamp?: string }
): HumanMessage {
  const additional_kwargs = {
    ...(options?.additional_kwargs ?? {}),
    ...(options?.timestamp ? { timestamp: options.timestamp } : {}),
    [INTENT_KWARG]: intent,
  };
  return new HumanMessage({
    content: describeIntent(intent),
    name: CONTEXT_MESSAGE_NAME,
    id: options?.id ?? crypto.randomUUID(),
    additional_kwargs,
  });
}

export const IntentMessage = {
  create: createIntentMessage,
  isInstance: isIntentMessage,
  /** Returns the intent payload if `msg` is an IntentMessage, else null. */
  payload<T = unknown>(msg: BaseMessage): T | null {
    if (!isIntentMessage(msg)) return null;
    return (msg.additional_kwargs?.[INTENT_KWARG] as T | undefined) ?? null;
  },
};

export const isFirstMessage = (record: RecordWithMessages): boolean => {
  if (!record.messages || record.messages.length === 0) {
    return false;
  }
  const humanMessages = record.messages.filter(isHumanMessage) as BaseMessage[];
  return humanMessages.length === 1;
};

// ---------------------------------------------------------------------------
// formatMessages — pretty-print a message array for debugging / test logs.
//
// Optimized for human + AI agent scanability when a test fails or a trace
// looks weird. Returns a multi-line string; not for user-facing rendering
// (use `userVisibleMessages` for that). Sample output:
//
//   [0] Human: Add a partial-work component.
//   [1] AI: Wrote partial work.
//       → write_file(file_path="src/components/PartialWork.tsx", …) [tc-ws-1]
//   [2] Tool[tc-ws-1]: File written successfully.
//   [3] system-reminder (BUG_ALERT_REMINDER): Build error — fix this first.
//   [4] AI ‹agent_runtime_error›: GraphRecursionError: recursion limit reached: 25
//   [5] AI: I hit a complexity limit before I could finish…
//
// Drop into a test like:
//   console.log(formatMessages(result.messages));
//
// Or assert against the output if you want a snapshot-style assertion:
//   expect(formatMessages(result.messages)).toMatchSnapshot();
// ---------------------------------------------------------------------------

export interface FormatMessagesOptions {
  /** Per-message character cap before truncating. Default 200. 0 disables. */
  truncate?: number;
  /** Render index prefix `[N]`. Default true. */
  showIndex?: boolean;
  /** Indent string for continuation / tool_call lines. Default 4 spaces. */
  indent?: string;
}

/** Pretty-print a message array. Pure debug helper, no UI semantics. */
export function formatMessages(
  messages: BaseMessage[],
  options: FormatMessagesOptions = {}
): string {
  const truncate = options.truncate ?? 200;
  const showIndex = options.showIndex ?? true;
  const indent = options.indent ?? "    ";

  return messages
    .map((msg, i) => formatOne(msg, i, { truncate, showIndex, indent }))
    .join("\n");
}

interface ResolvedFormatOpts {
  truncate: number;
  showIndex: boolean;
  indent: string;
}

function formatOne(msg: BaseMessage, idx: number, opts: ResolvedFormatOpts): string {
  const prefix = opts.showIndex ? `[${idx}] ` : "";
  const type = msg._getType();
  const tag = detectFormatTag(msg);

  // SystemReminder gets a richer label — the reminderType matters for
  // debugging (you want to see "BUG_ALERT" not just "system-reminder").
  if (SystemReminder.isInstance(msg)) {
    const reminderType = SystemReminder.getType(msg) ?? "unknown";
    const body = sanitizeContent(
      SystemReminder.unwrap(stringContent(msg)),
      opts
    );
    return `${prefix}system-reminder (${reminderType}): ${body}`;
  }

  // Other tagged messages — show the tag. For ToolMessages we keep the
  // tool_call_id too, because when a tool errors the id is the only
  // thing that ties it back to the AI message that asked for it.
  if (tag) {
    const inner = stripTag(tag, stringContent(msg));
    const body = sanitizeContent(inner, opts);
    if (type === "tool") {
      const tm = msg as ToolMessage;
      return `${prefix}Tool[${tm.tool_call_id ?? "no-id"}] ‹${tag}›: ${body}`;
    }
    const role = type === "ai" ? "AI" : type;
    return `${prefix}${role} ‹${tag}›: ${body}`;
  }

  // Plain messages.
  if (type === "human") {
    return `${prefix}Human: ${sanitizeContent(stringContent(msg), opts)}`;
  }

  if (type === "ai") {
    const aiMsg = msg as AIMessage;
    const text = sanitizeContent(stringContent(aiMsg), opts);
    const head = `${prefix}AI: ${text}`;
    const toolCalls = aiMsg.tool_calls ?? [];
    if (toolCalls.length === 0) return head;
    const callLines = toolCalls.map(
      (tc) =>
        `${opts.indent}→ ${tc.name}(${formatArgs(tc.args, opts)}) [${tc.id ?? "no-id"}]`
    );
    return [head, ...callLines].join("\n");
  }

  if (type === "tool") {
    const tm = msg as ToolMessage;
    const errorMark =
      tm.status === "error" ||
      (typeof tm.content === "string" && tm.content.includes("<tool_use_error>"))
        ? " ERROR"
        : "";
    return `${prefix}Tool[${tm.tool_call_id ?? "no-id"}]${errorMark}: ${sanitizeContent(stringContent(tm), opts)}`;
  }

  return `${prefix}${type}: ${sanitizeContent(stringContent(msg), opts)}`;
}

/** First text in the message, joining content-blocks if needed. */
function stringContent(msg: BaseMessage): string {
  const c = msg.content;
  if (typeof c === "string") return c;
  if (!Array.isArray(c)) return String(c);
  const parts: string[] = [];
  for (const block of c) {
    if (block && typeof block === "object") {
      const b = block as Record<string, unknown>;
      if (b.type === "text" && typeof b.text === "string") parts.push(b.text);
      else if (b.type === "image_url") parts.push("[image]");
      else if (b.type === "tool_use") parts.push(`[tool_use:${String(b.name ?? "?")}]`);
      else if (b.type === "tool_result") parts.push("[tool_result]");
      else if (b.type) parts.push(`[${String(b.type)}]`);
    }
  }
  return parts.join(" ");
}

/**
 * Collapse newlines + truncate. Multi-line content makes the formatted
 * output hard to scan; we keep things one-line-per-message so an
 * agent / human reader can see the conversation flow.
 */
function sanitizeContent(text: string, opts: ResolvedFormatOpts): string {
  const oneLine = text.replace(/\s+/g, " ").trim();
  if (opts.truncate <= 0 || oneLine.length <= opts.truncate) return oneLine;
  return `${oneLine.slice(0, opts.truncate)}… (+${oneLine.length - opts.truncate} chars)`;
}

/** Compact arg preview — only the keys + first-line values, length-capped. */
function formatArgs(args: unknown, opts: ResolvedFormatOpts): string {
  if (args == null || typeof args !== "object") return String(args);
  const entries = Object.entries(args as Record<string, unknown>);
  const perFieldCap = Math.max(40, Math.floor(opts.truncate / Math.max(1, entries.length)));
  const localOpts = { ...opts, truncate: perFieldCap };
  const parts = entries.map(([k, v]) => {
    if (typeof v === "string") {
      return `${k}="${sanitizeContent(v, localOpts)}"`;
    }
    if (v == null) return `${k}=${String(v)}`;
    if (typeof v === "object") {
      return `${k}=${sanitizeContent(JSON.stringify(v), localOpts)}`;
    }
    return `${k}=${String(v)}`;
  });
  return parts.join(", ");
}

/** Find the first registered tag whose `isInstance` matches `msg`. */
function detectFormatTag(msg: BaseMessage): string | null {
  for (const t of TAGGED_MESSAGE_REGISTRY) {
    // Skip SystemReminder here — it's handled with a richer label above.
    if (t.tag === SystemReminder.tag) continue;
    if (t.isInstance(msg)) return t.tag;
  }
  return null;
}

/** Strip `<tag>` and `</tag>` from a string content, if present. */
function stripTag(tag: string, text: string): string {
  const open = `<${tag}>`;
  const close = `</${tag}>`;
  if (!text.startsWith(open)) return text;
  const inner = text.slice(open.length);
  const end = inner.lastIndexOf(close);
  return (end === -1 ? inner : inner.slice(0, end)).trim();
}


export function lastMessageWithToolCalls(messages: ReadonlyArray<BaseMessage>): AIMessage | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (!AIMessage.isInstance(m)) continue;
    const calls = m.tool_calls ?? [];
    if (calls.length === 0) continue;
    // First AIMessage-with-tool_calls scanning backwards = the latest dispatch.
    return m;
  }
  return null;
}
